import { useMemo, useState } from 'react';
import { CopyButton } from '@Components/CopyButton';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { Appearance, Size } from '@Components/designConstants';
import { formatPercent } from '../data-qa-utils';
import {
  buildGqvGroundTruthSql,
  computeGqvAggregateDeviation,
  GqvGroundTruthWeek
} from '../gqv-ground-truth-sql';

interface GqvGroundTruthPanelProps {
  company: string;
  weeks: GqvGroundTruthWeek[];
  // dataset.reviewGranularity -- see buildGqvGroundTruthSql's dateTruncUnitFor doc
  // comment for why the canonical-side SQL must bucket by the same grain as `weeks`.
  reviewGranularity?: string;
}

// Reuses formatPercent for the digits+% (rounding, decimal formatting), just adding
// the leading '+' formatPercent itself doesn't -- Justin's ask (2026-08-31): use the
// shared formatter where it fits rather than hand-rolling the whole thing.
const signedPct = (fraction: number) => `${fraction > 0 ? '+' : ''}${formatPercent(fraction, 1)}`;

// The compare-to-canonical (GQV) layer folded into Dependent Variable's Brand Equity
// GQV stage -- same pattern as Portfolio/Channels' GroundTruthSqlPanel (an
// always-visible deviation readout, no fixed pass/fail cutoff, plus a collapsible SQL
// block the reviewer runs themselves), just for a single metric instead of paired
// spend/impressions.
export const GqvGroundTruthPanel = ({
  company,
  weeks,
  reviewGranularity
}: GqvGroundTruthPanelProps) => {
  const [open, setOpen] = useState(false);
  const deviation = useMemo(() => computeGqvAggregateDeviation(weeks), [weeks]);
  const sql = useMemo(
    () => buildGqvGroundTruthSql({ company, weeks, reviewGranularity }),
    [company, weeks, reviewGranularity]
  );

  return (
    <div className='data-qa-gt'>
      {deviation !== undefined && (
        <div className='data-qa-gt__deviation'>
          MMM vs. canonical (GQV), training window total: {signedPct(deviation)}
        </div>
      )}

      <Button
        appearance={Appearance.EMPTY}
        size={Size.SM}
        className='data-qa-gt__toggle'
        aria-expanded={open}
        leadingElement={open ? <MdExpandLess /> : <MdExpandMore />}
        onClick={() => setOpen(o => !o)}
      >
        Compare to canonical (GQV)
      </Button>

      {open && (
        <div className='data-qa-gt__detail'>
          <div className='data-qa-gt__sql'>
            <div className='data-qa-gt__sql-header'>
              <div className='data-qa-gt__sql-label'>
                Run in Redshift to reproduce the canonical GQV total for each week above.
              </div>
              <CopyButton text={sql} aria-label='Copy SQL to clipboard' tooltipLabel='Copy SQL' />
            </div>
            <pre>{sql}</pre>
          </div>
        </div>
      )}
    </div>
  );
};
